import React, { useState, useEffect } from "react";
import { Table, Button, Input, Upload, message, Popconfirm, Space } from "antd";
import { db, collection, addDoc } from "../../firebaseConfig";
import { useNavigate } from "react-router-dom";
import { deleteDoc, doc, getDocs, updateDoc } from "firebase/firestore";
import * as XLSX from "xlsx";
import "./speakers.css";

type Speaker = {
  key: string;
  name: string;
  role: string;
  company: string;
  shortBio: string;
  fullBio: string;
  imageUrl: string;
  timestamp: any;
};

const ManageSpeakers: React.FC = () => {
  const navigate = useNavigate();
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [filteredSpeakers, setFilteredSpeakers] = useState<Speaker[]>([]);
  const [searchText, setSearchText] = useState("");
  const [newSpeaker, setNewSpeaker] = useState<Speaker>({
    key: "",
    name: "",
    role: "",
    company: "",
    shortBio: "",
    fullBio: "",
    imageUrl: "",
    timestamp: new Date(),
  });

  useEffect(() => {
    const isAuthenticated = localStorage.getItem("isAuthenticated");
    if (!isAuthenticated) navigate("/login");
  }, [navigate]);

  const fetchSpeakers = async () => {
    const querySnapshot = await getDocs(collection(db, "speakers"));
    let speakersData: Speaker[] = querySnapshot.docs.map((docSnap) => {
      const data = docSnap.data() as any;
      return {
        key: docSnap.id,
        name: data.name ?? "",
        role: data.role ?? "",
        company: data.company ?? "",
        shortBio: data.shortBio ?? "",
        fullBio: data.fullBio ?? "",
        imageUrl: data.imageUrl ?? "",
        timestamp:
          data.timestamp?.toDate?.() ??
          (typeof data.timestamp === "string"
            ? new Date(data.timestamp)
            : data.timestamp ?? new Date()),
      };
    });

    // ✅ Helper to remove titles like H.E., Dr., Mr., Ms., etc.
    const normalizeName = (name: string) =>
      name
        .replace(/^(H\.E\.|HE|Dr\.|Mr\.|Ms\.|Mrs\.)\s*/i, "") // remove leading titles
        .trim()
        .toLowerCase();

    // ✅ Sort alphabetically ignoring titles
    speakersData = speakersData.sort((a, b) =>
      normalizeName(a.name).localeCompare(normalizeName(b.name))
    );

    setSpeakers(speakersData);
    setFilteredSpeakers(speakersData);
  };

  const handleAddSpeaker = async () => {
    if (!newSpeaker.name || !newSpeaker.role || !newSpeaker.company) {
      message.error("Please fill out all the fields!");
      return;
    }
    try {
      await addDoc(collection(db, "speakers"), {
        ...newSpeaker,
      });
      setNewSpeaker({
        key: "",
        name: "",
        role: "",
        company: "",
        shortBio: "",
        fullBio: "",
        imageUrl: "",
        timestamp: new Date(),
      });
      fetchSpeakers();
    } catch (error) {
      console.error("Error adding speaker:", error);
    }
  };

  const handleEditSpeaker = async (record: Speaker) => {
    setNewSpeaker(record);
  };

  const handleUpdateSpeaker = async () => {
    try {
      const speakerRef = doc(db, "speakers", newSpeaker.key);
      await updateDoc(speakerRef, newSpeaker);
      message.success("Speaker updated successfully!");
      setNewSpeaker({
        key: "",
        name: "",
        role: "",
        company: "",
        shortBio: "",
        fullBio: "",
        imageUrl: "",
        timestamp: new Date(),
      });
      fetchSpeakers();
    } catch (error) {
      console.error("Error updating speaker:", error);
      message.error("Failed to update speaker.");
    }
  };

  const handleDeleteSpeaker = async (key: string) => {
    try {
      await deleteDoc(doc(db, "speakers", key));
      message.success("Speaker deleted successfully!");
      fetchSpeakers();
    } catch (error) {
      console.error("Error deleting speaker:", error);
      message.error("Failed to delete speaker.");
    }
  };

  // Filter speakers as user types
  useEffect(() => {
    const filtered = speakers.filter((s) => {
      const query = searchText.toLowerCase();
      return (
        s.name.toLowerCase().includes(query) ||
        s.role.toLowerCase().includes(query) ||
        s.company.toLowerCase().includes(query)
      );
    });
    setFilteredSpeakers(filtered);
  }, [searchText, speakers]);

  useEffect(() => {
    fetchSpeakers();
  }, []);

  // Export to Excel
  const handleExportToExcel = () => {
    if (!speakers.length) {
      message.info("No speakers to export.");
      return;
    }
    const rows = speakers.map((s) => ({
      Name: s.name,
      Role: s.role,
      Company: s.company,
      "Short Bio": s.shortBio,
      "Full Bio": s.fullBio,
      "Image URL": s.imageUrl,
      "Created/Updated At":
        s.timestamp instanceof Date
          ? s.timestamp.toISOString()
          : String(s.timestamp ?? ""),
      ID: s.key,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Speakers");
    XLSX.writeFile(wb, "speakers.xlsx");
  };

  const speakerColumns = [
    {
      title: "Image",
      key: "imageUrl",
      render: (record: Speaker) => (
        <img
          src={record.imageUrl}
          alt="Speaker"
          style={{ width: 400, height: 100, objectFit: "contain" }}
        />
      ),
    },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Role", dataIndex: "role", key: "role" },
    { title: "Company", dataIndex: "company", key: "company" },
    { title: "Short Bio", dataIndex: "shortBio", key: "shortBio" },
    { title: "Full Bio", dataIndex: "fullBio", key: "fullBio" },
    {
      title: "Actions",
      key: "actions",
      render: (record: Speaker) => (
        <>
          <Button type="link" onClick={() => handleEditSpeaker(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure?"
            onConfirm={() => handleDeleteSpeaker(record.key)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  const isMobile = window.innerWidth <= 768;

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <h1 className="title" style={{ color: "#2e3c52" }}>
          Manage Speakers
        </h1>
        <Button onClick={handleExportToExcel}>Export to Excel</Button>
      </div>

      <div
        className="edit-modal-field"
        style={{ marginTop: 20, marginBottom: 20 }}
      >
        <Input
          placeholder="Search..."
          style={{ width: isMobile ? 350 : 800, height: 53, marginBottom: 25 }}
          allowClear
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Input
          placeholder="Name"
          value={newSpeaker.name}
          onChange={(e) =>
            setNewSpeaker({ ...newSpeaker, name: e.target.value })
          }
          style={{ marginBottom: 10, color: "#000" }}
        />
        <Input
          placeholder="Role"
          value={newSpeaker.role}
          onChange={(e) =>
            setNewSpeaker({ ...newSpeaker, role: e.target.value })
          }
          style={{ marginBottom: 10, color: "#000" }}
        />
        <Input
          placeholder="Company"
          value={newSpeaker.company}
          onChange={(e) =>
            setNewSpeaker({ ...newSpeaker, company: e.target.value })
          }
          style={{ marginBottom: 10, color: "#000" }}
        />
        <Input
          placeholder="Image Url (Cloudinary)"
          value={newSpeaker.imageUrl}
          onChange={(e) =>
            setNewSpeaker({ ...newSpeaker, imageUrl: e.target.value })
          }
          style={{ marginBottom: 10, color: "#000" }}
        />
        <Input.TextArea
          placeholder="Short Bio"
          value={newSpeaker.shortBio}
          onChange={(e) =>
            setNewSpeaker({ ...newSpeaker, shortBio: e.target.value })
          }
          style={{ marginBottom: 10 }}
        />
        <Input.TextArea
          placeholder="Full Bio"
          value={newSpeaker.fullBio}
          onChange={(e) =>
            setNewSpeaker({ ...newSpeaker, fullBio: e.target.value })
          }
          style={{ marginBottom: 10 }}
        />
        {newSpeaker.key ? (
          <Button
            className="btn btn-register mb-5"
            onClick={handleUpdateSpeaker}
            style={{ marginTop: 10 }}
          >
            Update Speaker
          </Button>
        ) : (
          <Button
            className="btn btn-register mb-5"
            onClick={handleAddSpeaker}
            style={{ marginTop: 10 }}
          >
            Add Speaker
          </Button>
        )}
      </div>

      <Table
        columns={speakerColumns}
        dataSource={filteredSpeakers}
        pagination={{ pageSize: 5 }}
        bordered
      />
    </div>
  );
};

export default ManageSpeakers;
